import {
  Chip,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { EmptyState, Progress } from '@backstage/core-components';
import { Alert } from '@material-ui/lab';
import { useUpwindResources } from './useUpwindResources';
import type { UpwindResource } from './upwindTypes';

const UPWIND_CONSOLE_URL = 'https://console.upwind.io';

function ExposureBadge({ type }: { type: string }) {
  return <Chip size="small" label={type !== 'none' ? type : 'None'} />;
}

function ResourceRow({ resource }: { resource: UpwindResource }) {
  const criticalVulns = resource.vulnerability_risk.critical_count ?? 0;
  const criticalDetections = resource.detection_risk.critical_count ?? 0;

  return (
    <TableRow>
      <TableCell>
        <Link href={UPWIND_CONSOLE_URL} target="_blank" rel="noopener noreferrer">
          {resource.name}
        </Link>
      </TableCell>
      <TableCell>{resource.resource_type}</TableCell>
      <TableCell>{resource.cloud_account_id}</TableCell>
      <TableCell>
        <Chip
          size="small"
          label={criticalVulns}
          color={criticalVulns > 0 ? 'secondary' : 'default'}
        />
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          label={criticalDetections}
          color={criticalDetections > 0 ? 'secondary' : 'default'}
        />
      </TableCell>
      <TableCell>
        <ExposureBadge type={resource.network_exposure_type} />
      </TableCell>
    </TableRow>
  );
}

export function EntityUpwindContent() {
  const { loading, error, value } = useUpwindResources();

  const resources = value?.resources ?? [];

  if (error && !resources.length) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  if (loading && !resources.length) return <Progress />;

  if (resources.length === 0) {
    return (
      <EmptyState
        title="No Upwind resources found"
        missing="data"
        description="No Upwind-monitored resources are tagged to this entity. Check that the backstage_scid tag is applied in Upwind."
      />
    );
  }

  return (
    <>
      {error && <Alert severity="warning" style={{ marginBottom: 16 }}>{error.message}</Alert>}
      <Typography variant="body2" gutterBottom style={{ marginBottom: 16 }}>
        {resources.length} resource{resources.length !== 1 ? 's' : ''} monitored by Upwind
      </Typography>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Resource</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Cloud Account</TableCell>
              <TableCell>Critical Vulns</TableCell>
              <TableCell>Critical Detections</TableCell>
              <TableCell>Network Exposure</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {resources.map(r => (
              <ResourceRow key={r.id} resource={r} />
            ))}
          </TableBody>
        </Table>
      </Paper>
    </>
  );
}
